import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { CsrfService } from '../auth/csrf.service';
import { DalOptions, DAL_OPTIONS } from './dal-options.token';

export type PolpoApiGet = <T>(path: string, params?: HttpParams) => Observable<T>;
export type PolpoApiPut = <T>(path: string, params?: HttpParams, body?: object) => Observable<T>;
export type PolpoApiDelete = <T>(path: string, params?: HttpParams) => Observable<T>;
export type PolpoApiPost = <T>(path: string, params?: HttpParams, body?: object) => Observable<T>;
export type PolpoApiPatch = <T>(path: string, params?: HttpParams, body?: object) => Observable<T>;

@Injectable({ providedIn: 'root' })
export class PolpoApi {
  private csrfService = inject<CsrfService>(CsrfService);

  private _defaultHttpOptions = { withCredentials: true };

  constructor(private httpClient: HttpClient, @Inject(DAL_OPTIONS) private options: DalOptions) {}

  public get<T>(model: string) {
    return (path: string, params?: HttpParams) => {
      const endpoint = this.getEndPoint(model, path);

      return this.httpClient.get<T>(endpoint, {
        ...this._defaultHttpOptions,
        ...(params ? { params } : {}),
      });
    };
  }

  public put<T>(model: string) {
    return (path: string, params?: HttpParams, body: object = {}) => {
      const endpoint = this.getEndPoint(model, path);

      return this.withCsrfHeaders$().pipe(
        switchMap((headers) =>
          this.httpClient.put<T>(endpoint, body, {
            ...this._defaultHttpOptions,
            ...(params ? { params } : {}),
            headers,
          })
        )
      );
    };
  }

  public delete<T>(model: string) {
    return (path: string, params?: HttpParams) => {
      const endpoint = this.getEndPoint(model, path);

      return this.withCsrfHeaders$().pipe(
        switchMap((headers) =>
          this.httpClient.delete<T>(endpoint, {
            ...this._defaultHttpOptions,
            ...(params ? { params } : {}),
            headers,
          })
        )
      );
    };
  }

  public post<T>(model: string) {
    return (path: string, params?: HttpParams, body: object = {}) => {
      const endpoint = this.getEndPoint(model, path);

      return this.withCsrfHeaders$().pipe(
        switchMap((headers) =>
          this.httpClient.post<T>(endpoint, body, {
            ...this._defaultHttpOptions,
            ...(params ? { params } : {}),
            headers,
          })
        )
      );
    };
  }

  public patch<T>(model: string) {
    return (path: string, params?: HttpParams, body: object = {}) => {
      const endpoint = this.getEndPoint(model, path);

      return this.withCsrfHeaders$().pipe(
        switchMap((headers) =>
          this.httpClient.patch<T>(endpoint, body, {
            ...this._defaultHttpOptions,
            ...(params ? { params } : {}),
            headers,
          })
        )
      );
    };
  }

  private withCsrfHeaders$() {
    return this.csrfService.getToken$().pipe(
      map((token) => ({
        // Do NOT set a 'null' or 'undefined' header, it will error and swallow the error
        // you will not see anything in your network tab and you will be questioning your sanity
        // ask me how I know
        ...(token ? { 'x-csrf-token': token } : {}),
      }))
    );
  }

  private getEndPoint(model: string, path: string) {
    if (!path) return `${this.options.apiBaseUrl}/api/${model}`;
    return `${this.options.apiBaseUrl}/api/${model}/${path}`;
  }
}
